/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description

  This class is the look angles container wrapper.

  - It has a top-level styled component that serves as the root for applying styles to child elements.
  - It has a context provider that is provides the Terminal Details Object to the child components

 */

import React from 'react';
import LookAnglesContainer from './LookAnglesContainer';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import styled from 'styled-components';
import StyledProps from '../../../common/theme/AppThemeInterface';
import { HighlightedSatelliteInFov } from '../TabbedPanel/TerminalTabPanel';
import { Box } from '@mui/material';
import { TerminalPlatformsEnum } from '../../../../shared/entities/terminalPlatform';
import { isPlatformTypeInList } from '../../../../utils/PlatformTypeUtils';

const DEFAULT_HEIGHT = '235px';
const GROUND_TERMINAL_HEIGHT = '188px';
const DEFAULT_FOOTER_MARGIN_TOP = '198px';

export const TerminalDetailsContext = React.createContext({} as TerminalDetails);

export interface LookAnglesContainerWrapperProps {
  terminalDetails?: TerminalDetails;
  isOverviewMode: boolean;
  satellitesInFOVIndexOffset?: number;
  highlightedSatelliteInFov: HighlightedSatelliteInFov | undefined;
  setHighlightedSatelliteInFov: Function;
}

export interface LookAnglesContainerWrapperState {}

interface StyledLookAnglesContainerWrapperProps extends StyledProps {}

const StyledLookAnglesContainerWrapper = styled.div`
  display: block;
  

  overflow: hidden;
  background-color: ${({ theme }: StyledLookAnglesContainerWrapperProps) => theme.secondaryBackgroundColor} !important;

  .look__angles__header__metrics {
    padding-top: 10px;
    height: 42px;
    width: 100%;
    box-sizing: content-box;
  }
  .look__angles__footer__metrics {
    position: absolute;
    margin-top: ${DEFAULT_FOOTER_MARGIN_TOP};
    height: 42px;
    width: 100%;
  }

  .look__angles__metric__container {
    font-size: 14px;
    display: inline-block;
    position: absolute;
    font-weight: 600;
    line-height: 20px;

    .header {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${({ theme }: StyledLookAnglesContainerWrapperProps) => theme.primaryTextColor};
    }
  }
  .look__angles__metric__container.heading {
    left: 20px;
  }
  .look__angles__metric__container.roll {
    left: 139px;
  }
  .look__angles__metric__container.pitch {
    left: 259px;
  }
  .look__angles__metric__container.beam {
    left: 10px;
    position: relative;
   width: 125px;
   max-width: 125px;
   .value {
      position: relative;
    }
  }
  .look__angles__metric__container.azimuth {
    left: 144px;
  }
  .look__angles__metric__container.elevation {
    left: 225px;
  }

  .compass__container {
    position: relative;
    .north__label {
      min-width: 100%;
      top: 12px;
      font-size: 14px;
      position: absolute;
      text-align: center;
      line-height: 20px;
    }
    .heading__unknown__tooltip {
      min-width: 288px;
      top: 166px;
      left: 35px;
      opacity: 0.8;
      font-size: 12px;
      position: absolute;
      text-align: center;
    }}
    .hovered__alternative__satellite__name {
      min-width: 100%;
      top: 184px;
      opacity: 0.8;
      font-size: 12px;
      position: absolute;
      text-align: center;
    }
    .compass {
      position: absolute;
      top: 32px;
      left: 88px;
    }

    .look__angles__dynamics__container {
      width: 148px;
      height: 148px;
      position: absolute;
      top: 26px;
      left: 106px;
      .look__angles__current__look_angle__graphic {
        width: 148px;
        height: 148px;
      }
    }
  }

  .compass__background {
    position: absolute;
    top: 32px;
    left: 88px;
  }

  .compass__container--ground_terminal {
    .north__label {
      visibility: visible;
    }

    .look__angles__compass__tick__marks {
      visibility: visible;
    }

    .look__angles__compass__north__line { 
      visibility: visible;
    }
  }
  
  .ground__terminal__icon__path {
    fill: #ffffff;
  }
`;

function LookAnglesContainerWrapper({
  terminalDetails,
  isOverviewMode,
  satellitesInFOVIndexOffset = 0,
  highlightedSatelliteInFov,
  setHighlightedSatelliteInFov,
}: LookAnglesContainerWrapperProps) {
  return (
    <StyledLookAnglesContainerWrapper>
      <Box
        height={
          isPlatformTypeInList(
            (terminalDetails || {}).platformType || TerminalPlatformsEnum.Airborne,
            TerminalPlatformsEnum.Airborne,
          )
            ? DEFAULT_HEIGHT
            : GROUND_TERMINAL_HEIGHT
        }
        pt={
          isPlatformTypeInList(
            (terminalDetails || {}).platformType || TerminalPlatformsEnum.Airborne,
            TerminalPlatformsEnum.Airborne,
          )
            ? 0
            : '10px'
        }
      >
        <TerminalDetailsContext.Provider value={terminalDetails || ({} as TerminalDetails)}>
          <LookAnglesContainer
            isOverviewMode={isOverviewMode}
            satellitesInFOVIndexOffset={satellitesInFOVIndexOffset}
            highlightedSatelliteInFov={highlightedSatelliteInFov}
            setHighlightedSatelliteInFov={setHighlightedSatelliteInFov}
          />
        </TerminalDetailsContext.Provider>
      </Box>
    </StyledLookAnglesContainerWrapper>
  );
}

export default LookAnglesContainerWrapper;
